const selectors = {
  drawerOpen: '[data-drawer-open]',
  drawerClose: '[data-drawer-close]',
  drawer: '[data-drawer]',
  drawerBackdrop: '[data-drawer-backdrop]',
  productForm: '[data-product-form]',
  quickAdd: '[data-quick-add]',
  galleryThumb: '[data-gallery-thumb]',
  galleryTrack: '[data-gallery-track]',
  gallerySlide: '[data-gallery-slide]',
  fadeSection: '.fade-section',
};

function money(cents, currency) {
  return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
    style: 'currency',
    currency: currency || window.Shopify?.currency?.active || 'EUR',
  }).format(Number(cents || 0) / 100);
}

function openDrawer(name) {
  document.querySelector(`[data-drawer="${name}"]`)?.classList.add('is-open');
  document.querySelector(`[data-drawer-backdrop="${name}"]`)?.classList.add('is-open');
  document.documentElement.classList.add('drawer-open');
}

function closeDrawer(name) {
  document.querySelector(`[data-drawer="${name}"]`)?.classList.remove('is-open');
  document.querySelector(`[data-drawer-backdrop="${name}"]`)?.classList.remove('is-open');
  document.documentElement.classList.remove('drawer-open');
}

function initDrawers() {
  document.addEventListener('click', (event) => {
    const open = event.target.closest(selectors.drawerOpen);
    const close = event.target.closest(selectors.drawerClose);
    const backdrop = event.target.closest(selectors.drawerBackdrop);

    if (open) {
      event.preventDefault();
      openDrawer(open.dataset.drawerOpen);
    }

    if (close) {
      // Links with data-drawer-close must still navigate.
      if (close.tagName !== 'A') event.preventDefault();
      closeDrawer(close.dataset.drawerClose);
    }

    if (backdrop) {
      closeDrawer(backdrop.dataset.drawerBackdrop);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      document.querySelectorAll(selectors.drawer).forEach((drawer) => closeDrawer(drawer.dataset.drawer));
    }
  });
}

async function refreshCartDrawer() {
  const target = document.querySelector('[data-cart-drawer-content]');
  if (!target) return;

  const response = await fetch(`${window.Shopify.routes.root}?sections=organiqly-cart-drawer`);
  const json = await response.json();
  const html = json['organiqly-cart-drawer'];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const updated = doc.querySelector('[data-cart-drawer-content]');
  if (updated) target.innerHTML = updated.innerHTML;

  const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then((r) => r.json());
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = cart.item_count;
    el.classList.toggle('is-empty', cart.item_count === 0);
  });
}

function initCartLineQuantity() {
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cart-line-qty] [data-cart-qty-step]');
    if (!button) return;

    const wrap = button.closest('[data-cart-line-qty]');
    const value = wrap.querySelector('[data-cart-qty-value]');
    const quantity = Math.max(0, parseInt(value.textContent, 10) + Number(button.dataset.cartQtyStep));

    wrap.querySelectorAll('button').forEach((b) => (b.disabled = true));
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: wrap.dataset.lineKey, quantity }),
      });
      if (!response.ok) {
        showToast(document.documentElement.dataset.cartErrorLabel);
        return;
      }
      await refreshCartDrawer();
    } catch (error) {
      showToast(document.documentElement.dataset.cartErrorLabel);
    } finally {
      wrap.querySelectorAll('button').forEach((b) => (b.disabled = false));
    }
  });
}

let toastTimer;

function showToast(message, variant = 'error') {
  if (!message) return;

  let toast = document.querySelector('[data-toast]');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'toast';
    toast.setAttribute('data-toast', '');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }

  toast.textContent = message;
  toast.classList.toggle('toast--error', variant === 'error');
  // restart the enter animation even if a toast is already showing
  toast.classList.remove('is-visible');
  void toast.offsetWidth;
  toast.classList.add('is-visible');

  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 4000);
}

function showCartError(message) {
  if (message) showToast(message, 'error');
}

async function addVariantToCart(variantId, quantity = 1) {
  showCartError('');
  try {
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: variantId, quantity }),
    });

    if (!response.ok) {
      let message = '';
      try {
        const data = await response.json();
        message = data?.description || data?.message || '';
      } catch (parseError) {
        message = '';
      }
      showCartError(message || document.documentElement.dataset.cartErrorLabel || 'Could not add to cart. Please try again.');
      return false;
    }

    await refreshCartDrawer();
    openDrawer('cart');
    return true;
  } catch (error) {
    showCartError(document.documentElement.dataset.cartErrorLabel || 'Could not add to cart. Please try again.');
    return false;
  }
}

function selectedOptions(form) {
  const values = [];
  form.querySelectorAll('[data-option-position]').forEach((group) => {
    const selected = group.querySelector('[data-option-value].is-active');
    values[Number(group.dataset.optionPosition) - 1] = selected?.dataset.optionValue;
  });
  return values;
}

function matchingVariant(variants, values) {
  return variants.find((variant) => {
    return variant.options.every((value, index) => value === values[index]);
  });
}

function setActiveOption(button) {
  const group = button.closest('[data-option-position]');
  if (!group) return;
  group.querySelectorAll('[data-option-value]').forEach((item) => item.classList.remove('is-active'));
  button.classList.add('is-active');
}

function formatUnitPrice(variant) {
  const m = variant?.unit_price_measurement;
  if (variant?.unit_price && m && m.reference_value) {
    const ref = m.reference_value === 1 ? '' : `${m.reference_value} `;
    return `${money(variant.unit_price, variant.currency)} / ${ref}${m.reference_unit}`;
  }
  if (Number(variant?.grams) > 0) {
    const perKg = Math.round(Number(variant.price) * 1000 / Number(variant.grams));
    return `${money(perKg, variant.currency)} / kg`;
  }
  return '';
}

function showVariantState(form, variant) {
  const idInput = form.querySelector('[name="id"]');
  const addArea = form.querySelector('[data-add-area]');
  const notifyForm = form.querySelector('[data-notify-form]');
  const price = form.querySelector('[data-product-price]');
  const totalPrice = form.querySelector('[data-product-total-price]');
  const quantity = form.querySelector('[name="quantity"]');
  const vorteilspack = form.querySelector('[data-vorteilspack]');
  // The sticky add-to-cart bar mirrors ONLY the main PDP form; quick-view and
  // featured-product forms must never change what it adds to the cart.
  const isAtcSource = form.hasAttribute('data-atc-source');
  const stickyVariant = isAtcSource ? document.querySelector('[data-sticky-variant-id]') : null;
  const stickySubmit = isAtcSource ? document.querySelector('[data-sticky-submit]') : null;

  if (idInput && variant) idInput.value = variant.id;
  if (stickyVariant && variant) stickyVariant.value = variant.id;
  if (price && variant) price.textContent = money(variant.price, variant.currency);
  const unitPrice = form.querySelector('.product-price__meta [data-unit-price]');
  if (unitPrice && variant) {
    const text = formatUnitPrice(variant);
    unitPrice.textContent = text;
    unitPrice.hidden = !text;
  }
  if (totalPrice && variant) totalPrice.textContent = money(Number(variant.price) * Number(quantity?.value || 1), variant.currency);
  const netQuantity = document.querySelector('[data-net-quantity]');
  if (netQuantity && variant?.title && isAtcSource) netQuantity.textContent = variant.title;
  if (form && variant) {
    form.dataset.currentPrice = variant.price;
    form.dataset.currentCurrency = variant.currency;
  }

  const available = !!variant?.available;
  addArea?.classList.toggle('is-hidden', !available);
  notifyForm?.classList.toggle('is-active', !available);
  vorteilspack?.classList.toggle('is-hidden', !variant?.isVorteilspack);
  if (stickySubmit) stickySubmit.disabled = !available;

  form.querySelectorAll('[data-tier-variant-id]').forEach((tier) => {
    tier.classList.toggle('is-active', variant && tier.dataset.tierVariantId === String(variant.id));
  });
}

function updateProductTotal(form) {
  const totalPrice = form.querySelector('[data-product-total-price]');
  const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
  const price = Number(form.dataset.currentPrice || 0);
  if (totalPrice && price) totalPrice.textContent = money(price * quantity, form.dataset.currentCurrency);
}

function initProductForms() {
  document.querySelectorAll(selectors.productForm).forEach((form) => {
    const variants = JSON.parse(form.querySelector('[data-variants-json]')?.textContent || '[]');

    form.addEventListener('click', (event) => {
      const optionButton = event.target.closest('[data-option-value]');
      const tierButton = event.target.closest('[data-tier-variant-id]');

      if (optionButton) {
        setActiveOption(optionButton);
        const variant = matchingVariant(variants, selectedOptions(form));
        showVariantState(form, variant);
      }

      if (tierButton) {
        event.preventDefault();
        const variant = variants.find((item) => String(item.id) === tierButton.dataset.tierVariantId);
        if (!variant) return;
        const sizeOption = form.querySelector('[data-size-option-position]');
        if (sizeOption) {
          const position = Number(sizeOption.dataset.sizeOptionPosition);
          const value = variant.options[position - 1];
          const optionButtonForTier = form.querySelector(`[data-option-position="${position}"] [data-option-value="${CSS.escape(value)}"]`);
          if (optionButtonForTier) setActiveOption(optionButtonForTier);
        }
        showVariantState(form, variant);
      }
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const formData = new FormData(form);
      const variantId = formData.get('id');
      const quantity = Number(formData.get('quantity') || 1);
      if (!variantId) return;
      const variant = variants.find((item) => String(item.id) === String(variantId));
      if (!variant?.available) return;
      await addVariantToCart(variantId, quantity);
    });

    form.addEventListener('input', (event) => {
      if (event.target.closest('[name="quantity"]')) updateProductTotal(form);
    });

    form.addEventListener('click', (event) => {
      const quantityButton = event.target.closest('.quantity button');
      if (quantityButton) requestAnimationFrame(() => updateProductTotal(form));
    });

    const initial = variants.find((variant) => String(variant.id) === String(form.querySelector('[name="id"]')?.value));
    showVariantState(form, initial);
  });
}

function initQuickAdd() {
  document.addEventListener('click', async (event) => {
    const button = event.target.closest(selectors.quickAdd);
    if (!button) return;
    event.preventDefault();
    if (button.disabled || !button.dataset.variantId) return;
    await addVariantToCart(button.dataset.variantId, 1);
  });
}

function initGallery() {
  document.querySelectorAll('[data-gallery]').forEach((gallery) => {
    const track = gallery.querySelector(selectors.galleryTrack);
    const slides = Array.from(gallery.querySelectorAll(selectors.gallerySlide));
    const thumbs = Array.from(gallery.querySelectorAll(selectors.galleryThumb));
    if (!track || slides.length < 2) return;

    const setActive = (index) => {
      thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      });
    };

    // Thumb click → scroll the track (native scroll-snap handles the motion).
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = Number(thumb.dataset.index) || 0;
        track.scrollTo({ left: slides[index].offsetLeft, behavior: 'smooth' });
        setActive(index);
      });
    });

    // Swipe/scroll → keep the active thumb in sync with the visible slide.
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActive(Number(entry.target.dataset.index) || 0);
      });
    }, { root: track, threshold: 0.6 });
    slides.forEach((slide) => observer.observe(slide));
  });
}

function initStickyAtc() {
  const source = document.querySelector('[data-atc-source]');
  const sticky = document.querySelector('[data-sticky-atc]');
  const stickyForm = document.querySelector('[data-sticky-form]');
  if (!source || !sticky || !stickyForm) return;

  // Only show the sticky bar once the shopper has scrolled PAST the main
  // add-to-cart form (its bottom edge is above the viewport). On load, the form
  // sits below the fold on mobile, which must not count as "passed".
  const observer = new IntersectionObserver(([entry]) => {
    const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
    sticky.classList.toggle('is-visible', scrolledPast);
  }, { threshold: 0 });

  observer.observe(source);

  stickyForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    const variantId = stickyForm.querySelector('[name="id"]')?.value;
    if (!variantId) return;
    await addVariantToCart(variantId, 1);
  });
}

function initNotifyForms() {
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-notify-form]');
    if (!form) return;
    event.preventDefault();
    const emailField = form.querySelector('[type="email"]');
    const emailValue = emailField?.value.trim() || '';
    if (!emailValue || !emailValue.includes('@')) {
      emailField?.focus();
      return;
    }
    const payload = {
      email: emailValue,
      productHandle: form.dataset.productHandle,
      variantId: form.closest(selectors.productForm)?.querySelector('[name="id"]')?.value,
      variantTitle: form.closest(selectors.productForm)?.querySelector('[name="id"] option:checked')?.textContent,
    };
    document.dispatchEvent(new CustomEvent('organiqly:notify-interest', { detail: payload }));
    form.querySelector('[data-notify-message]').textContent = form.dataset.successMessage || 'Danke. Wir melden uns, sobald diese Variante verfügbar ist.';
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-notify-submit]');
    if (!button) return;
    const form = button.closest('[data-notify-form]');
    if (!form) return;
    const email = form.querySelector('[type="email"]');
    if (!email.reportValidity()) return;
    const payload = {
      email: email.value,
      productHandle: form.dataset.productHandle,
      variantId: form.closest(selectors.productForm)?.querySelector('[name="id"]')?.value,
    };
    document.dispatchEvent(new CustomEvent('organiqly:notify-interest', { detail: payload }));
    form.querySelector('[data-notify-message]').textContent = form.dataset.successMessage || 'Danke. Wir melden uns, sobald diese Variante verfügbar ist.';
  });
}

function initFadeSections() {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.15 });

  document.querySelectorAll(selectors.fadeSection).forEach((section) => observer.observe(section));

  // Theme editor: re-rendered sections come back as fresh nodes the observer
  // never saw — reveal them immediately so setting changes don't blank them.
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll(selectors.fadeSection).forEach((section) => section.classList.add('is-visible'));
    if (event.target.matches && event.target.matches(selectors.fadeSection)) {
      event.target.classList.add('is-visible');
    }
  });
}

function initReviewCarousel() {
  document.querySelectorAll('[data-review-carousel]').forEach((root) => {
    const track = root.querySelector('[data-review-track]');
    const viewport = root.querySelector('.review-carousel__viewport');
    const slides = [...root.querySelectorAll('[data-review-slide]')];
    const prev = root.querySelector('[data-review-prev]');
    const next = root.querySelector('[data-review-next]');
    if (!track || !slides.length) return;

    let index = 0;
    let timer = null;
    const AUTOPLAY_MS = 6000;

    // Columns, not slides: on mobile two stacked slides share one column.
    const columns = () => {
      const seen = [...new Set(slides.map((s) => Math.round(s.offsetLeft)))];
      return seen.sort((a, b) => a - b);
    };

    const perView = () => {
      const cols = columns();
      if (cols.length < 2) return 1;
      const step = cols[1] - cols[0];
      if (!step) return 1;
      return Math.max(1, Math.round(viewport.getBoundingClientRect().width / step));
    };

    const maxIndex = () => Math.max(0, columns().length - perView());

    const render = () => {
      const max = maxIndex();
      if (index < 0) index = max;
      if (index > max) index = 0;
      const cols = columns();
      const offset = cols[index] || 0;
      track.style.transform = 'translate3d(' + -offset + 'px, 0, 0)';
      const multi = cols.length > perView();
      if (prev) prev.disabled = !multi;
      if (next) next.disabled = !multi;
    };

    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const start = () => {
      stop();
      if (columns().length <= perView()) return;
      timer = setInterval(() => { index += 1; render(); }, AUTOPLAY_MS);
    };

    if (prev) prev.addEventListener('click', () => { index -= 1; render(); start(); });
    if (next) next.addEventListener('click', () => { index += 1; render(); start(); });

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { render(); start(); }, 150);
    });

    render();
    start();
  });
}

function initFeaturedProduct() {
  document.querySelectorAll('[data-featured-product]').forEach((root) => {
    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-tier-variant-id]');
      if (!button) return;
      root.querySelectorAll('[data-tier-variant-id]').forEach((item) => item.classList.remove('is-active'));
      button.classList.add('is-active');
    });
  });
}

function initLocalePicker() {
  document.querySelectorAll('[data-locale-picker]').forEach((root) => {
    const toggle = root.querySelector('[data-locale-toggle]');
    const panel = root.querySelector('[data-locale-panel]');
    const form = root.querySelector('form');
    const localeInput = root.querySelector('[data-locale-input]');
    const countryInput = root.querySelector('[data-country-input]');
    if (!toggle || !panel || !form) return;

    const close = () => {
      panel.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    };

    toggle.addEventListener('click', (event) => {
      event.stopPropagation();
      const open = panel.hidden;
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
    });

    panel.addEventListener('click', (event) => {
      const localeButton = event.target.closest('[data-locale-value]');
      const countryButton = event.target.closest('[data-country-value]');
      if (localeButton && localeInput) {
        localeInput.value = localeButton.dataset.localeValue;
        form.submit();
      }
      if (countryButton && countryInput) {
        countryInput.value = countryButton.dataset.countryValue;
        form.submit();
      }
    });

    document.addEventListener('click', (event) => {
      if (!root.contains(event.target)) close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });
  });
}

function initHeaderSearch() {
  const toggle = document.querySelector('[data-search-toggle]');
  const panel = document.querySelector('[data-search-panel]');
  if (!toggle || !panel) return;

  const input = panel.querySelector('[data-search-input]');
  const results = panel.querySelector('[data-search-results]');
  const closeButton = panel.querySelector('[data-search-close]');
  let timer;

  const close = () => {
    panel.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    if (results) results.innerHTML = '';
  };

  toggle.addEventListener('click', () => {
    const open = panel.hidden;
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    if (open && input) input.focus();
  });

  closeButton?.addEventListener('click', close);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !panel.hidden) close();
  });

  input?.addEventListener('input', () => {
    clearTimeout(timer);
    const term = input.value.trim();
    if (!results) return;
    if (term.length < 2) {
      results.innerHTML = '';
      return;
    }
    timer = setTimeout(async () => {
      try {
        const url = `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6`;
        const response = await fetch(url);
        if (!response.ok) return;
        const data = await response.json();
        const products = data?.resources?.results?.products || [];
        if (!products.length) {
          results.innerHTML = `<p class="header-search__empty">${results.dataset.emptyLabel || ''}</p>`;
          return;
        }
        results.innerHTML = products
          .map((product) => `
            <a class="header-search__result" href="${product.url}">
              <span class="header-search__result-media">${product.image ? `<img src="${product.image}" alt="" loading="lazy">` : ''}</span>
              <span class="header-search__result-title">${product.title}</span>
            </a>`)
          .join('');
      } catch (error) {
        results.innerHTML = '';
      }
    }, 250);
  });
}

function initQuantity() {
  document.querySelectorAll('[data-quantity]').forEach((root) => {
    const input = root.querySelector('[data-qty-input]');
    const value = root.querySelector('[data-qty-value]');
    if (!input || !value) return;

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-qty-step]');
      if (!button) return;
      const step = Number(button.dataset.qtyStep) || 0;
      const next = Math.max(1, (Number(input.value) || 1) + step);
      input.value = String(next);
      value.textContent = String(next);
      // keep any total/price logic listening on the form in sync
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
  });
}

function initQuickView() {
  const open = (handle) => {
    const modal = document.querySelector(`[data-quick-view="${CSS.escape(handle)}"]`);
    if (!modal) return;
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
  };

  const closeAll = () => {
    document.querySelectorAll('[data-quick-view]').forEach((modal) => { modal.hidden = true; });
    document.body.style.overflow = '';
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-quick-view-open]');
    if (opener) {
      event.preventDefault();
      open(opener.dataset.quickViewOpen);
      return;
    }
    if (event.target.closest('[data-quick-view-close]')) closeAll();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAll();
  });
}

function initProductUpsell() {
  document.querySelectorAll('[data-product-upsell]').forEach(async (root) => {
    const base = root.dataset.url;
    const target = root.querySelector('[data-product-upsell-list]');
    if (!base || !target) return;
    const load = async (intent) => {
      const response = await fetch(`${base}&intent=${intent}`);
      if (!response.ok) return null;
      const html = await response.text();
      const list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-product-upsell-list]');
      return list && list.querySelector('.product-upsell__item') ? list : null;
    };
    try {
      const list = (await load('complementary')) || (await load('related'));
      if (list) target.innerHTML = list.innerHTML;
    } catch (error) {
      /* keep the fallback already rendered */
    }
  });
}

function initProductRecommendations() {
  document.querySelectorAll('[data-product-recommendations]').forEach(async (root) => {
    const url = root.dataset.url;
    if (!url) return;
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const html = await response.text();
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-product-recommendations]');
      if (fresh && fresh.querySelector('.product-recommendations__item')) {
        root.innerHTML = fresh.innerHTML;
      }
    } catch (error) {
      /* keep the fallback already rendered */
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initProductRecommendations();
  initProductUpsell();
  initDrawers();
  initProductForms();
  initQuickAdd();
  initGallery();
  initStickyAtc();
  initNotifyForms();
  initFadeSections();
  initReviewCarousel();
  initFeaturedProduct();
  initLocalePicker();
  initHeaderSearch();
  initQuantity();
  initCartLineQuantity();
  initQuickView();
});
